import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  RestaurantNewResp,
  RestaurantNewVariables,
  RestaurantVariables,
  Restaurant
} from "../../types/restaurantTypes";

const CREATE_RESTAURANT = gql`
  mutation CreateRestaurant($attributes: CreateRestaurantInput!) {
    createRestaurant(attributes: $attributes) {
      restaurant {
        name
        phone
        website
        image
        wifi
      }
      errors
    }
  }
` 

type UseCreateRestaurantOptions = {
  onCompleted?: (restaurant: Restaurant) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<RestaurantNewResp, RestaurantNewVariables>>[1];
};

const useCreateRestaurant = (options?: UseCreateRestaurantOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    RestaurantNewResp,
    RestaurantNewVariables
  >(CREATE_RESTAURANT, options?.mutationOptions);

  const createRestaurant = useCallback(
    async (input: RestaurantVariables) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              phone: input.phone,
              website: input.website,
              name: input.name,
              image: input.image,
              wifi: input.wifi,
              address: {
                fullAddress: input.address.fullAddress,
                latitude: input.address.latitude,
                longitude: input.address.longitude,
                street: input.address.street,
                house: input.address.house,
                countryCode: input.address.countryCode,
                mapboxId: input.address.mapboxId
              }
            },
          },
        });

        const createdRestaurant = result?.data?.createRestaurant;

        if (createdRestaurant?.errors?.length) {
          throw new Error(createdRestaurant.errors.join(", "));
        }

        if (options?.onCompleted && createdRestaurant?.restaurant) {
          options.onCompleted(createdRestaurant.restaurant);
        }

        return createdRestaurant?.restaurant;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const restaurant = data?.createRestaurant?.restaurant;

  return {
    createRestaurant,
    createdRestaurant: restaurant,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useCreateRestaurant
